/**
 * CamEvidenceDrawer — side-by-side LOS vs CAM evidence viewer.
 *
 * The slide-out drawer the auditor opens when clicking "Evidence" on
 * any of the 12 rows in CamComparisonGrid. For each checklist point,
 * shows the actual LOS data column next to the actual CAM-extracted
 * data column with a verdict per field.
 *
 * The 12 renderers are intentionally point-specific because each check
 * compares different fields. Shared building blocks (VerdictHeader,
 * Section, KvRow, ComparisonTable) are inlined here so the drawer is
 * self-contained and doesn't take a refactoring dependency on the
 * existing 17-point EvidenceDrawer.
 *
 * Phase 4 scope: drawer + 12 renderers + jump-to-page on CAM PDF for
 * fields with extracted source.pageNumber.
 *
 * Not in scope (future phase): bounding-box highlights, secondary-PDF
 * upload UI (Legal Report / Valuation Report / CFA), full mismatch
 * resolution workflow (accept / waive / false-positive directly from
 * the drawer).
 */

import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  CheckCircle2, XCircle, MinusCircle, ShieldCheck,
  AlertTriangle, Info,
} from 'lucide-react';
import * as RadixDialog from '@radix-ui/react-dialog';
import { ArrowLeft, X } from 'lucide-react';
import { Badge } from '@/acc-ui/primitives/Badge';
import { mastersApi } from '@/api/other-apis';
import { PdfEmbed } from './PdfEmbed';
import type {
  FindingDTO, LosApplicationDataDTO, LoanFileParsingView,
} from '@loanaudit/shared';

// =========================================================================
// Public types
// =========================================================================

export type CamRowState =
  | 'matched'
  | 'deviation_justified'
  | 'mismatch'
  | 'inconclusive'
  | 'skipped';

export interface CamEvidenceDrawerProps {
  open: boolean;
  onClose: () => void;
  loanFileId: string;
  /** Checklist point number 1-12. Selects the renderer. */
  point: number | null;
  /** Short label like "CIBIL Score" — shown in the drawer title. */
  label: string;
  /** Row state — drives the verdict header color/badge. */
  state: CamRowState;
  /** The finding produced by the rule (if any). Null for matched rows. */
  finding: FindingDTO | null;
  /** LOS data attached to the loan file. */
  losData: LosApplicationDataDTO | null;
  /** Parsing view — contains CAM extracted fields + page numbers. */
  parsingView: LoanFileParsingView | null;
  /** Parsed documents on the loan file. */
  documents: Array<{
    id: string;
    originalFileName: string;
    documentType: string;
  }>;
  /** LOS-screen path string for the row, shown in the verdict header. */
  losPath: string;
}

// =========================================================================
// Shared helper to pull the CAM extracted fields out of parsingView
// =========================================================================

type CamFieldMap = Record<
  string,
  { value: unknown; confidence: number; source?: { pageNumber?: number } }
> | null;

interface CamData {
  /** The CAM document id (used for jump-to-page in PdfEmbed). */
  documentId: string | null;
  /** The CAM document filename. */
  fileName: string | null;
  /** Raw field map keyed by field name. */
  fields: CamFieldMap;
}

function getCamData(
  parsingView: LoanFileParsingView | null,
  documents: CamEvidenceDrawerProps['documents'],
): CamData {
  const docs = parsingView?.documents ?? [];
  const camDoc = docs.find((d) => d.classification?.predictedType === 'cam');
  if (!camDoc) {
    return { documentId: null, fileName: null, fields: null };
  }
  // Find filename via the documents list
  const docMeta = documents.find((d) => d.id === camDoc.documentId);
  return {
    documentId: camDoc.documentId,
    fileName: docMeta?.originalFileName ?? camDoc.fileName ?? 'CAM.pdf',
    fields: (camDoc.extraction?.fields as CamFieldMap) ?? null,
  };
}

function getCamField<T = unknown>(fields: CamFieldMap, name: string): T | null {
  if (!fields) return null;
  const f = fields[name];
  if (!f) return null;
  return (f.value ?? null) as T | null;
}

function getCamPage(fields: CamFieldMap, name: string): number | null {
  if (!fields) return null;
  return fields[name]?.source?.pageNumber ?? null;
}

// =========================================================================
// Helper to extract fields from a parsed document by its document type.
// Used to pull Legal Report and Valuation Report extracted values into
// the Evidence drawer for 3-way comparison (LOS vs CAM vs Report PDF).
// =========================================================================

/** Convert document type codes to professional display names. */
function formatDocTypeName(dt: string): string {
  const map: Record<string, string> = {
    cam: 'CAM Report',
    cfa_excel: 'CFA Excel Sheet',
    salary_slip: 'Salary Slip',
    bank_statement: 'Bank Statement',
    cibil_report: 'CIBIL Report',
    legal_technical_report: 'Legal Report',
    legal_report: 'Legal Report',
    valuation: 'Valuation Report',
    valuation_report: 'Valuation Report',
    kyc: 'KYC Documents',
    kyc_report: 'KYC Report',
    aadhaar: 'Aadhaar Card',
    pan: 'PAN Card',
    passport: 'Passport',
    voter_id: 'Voter ID',
    driving_licence: 'Driving Licence',
    itr: 'Income Tax Return',
    form_26as: 'Form 26AS',
    sanction_letter: 'Sanction Letter',
    kfs: 'Key Fact Statement',
    loan_agreement: 'Loan Agreement',
    application_form: 'Application Form',
    other: 'Other',
    unclassified: 'Unclassified',
  };
  return map[dt.toLowerCase()] ?? dt.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function getDocFields(
  parsingView: LoanFileParsingView | null,
  docType: string,
): CamFieldMap {
  if (!parsingView) return null;
  const docs = parsingView.documents ?? [];
  const doc = docs.find(
    (d) => d.classification?.predictedType === docType,
  );
  if (!doc?.extraction?.fields) return null;
  return doc.extraction.fields as CamFieldMap;
}

function getDocField<T = unknown>(fields: CamFieldMap, name: string): T | null {
  if (!fields) return null;
  const f = fields[name];
  if (!f) return null;
  return (f.value ?? null) as T | null;
}

// =========================================================================
// Inline UI helpers (kept private to this file by intent)
// =========================================================================

const STATE_META: Record<CamRowState, {
  label: string;
  icon: ReactNode;
  cls: string;
}> = {
  matched: {
    label: 'Matched',
    icon: <CheckCircle2 className="w-4 h-4 text-emerald-700" />,
    cls: 'bg-emerald-50 border-emerald-200',
  },
  deviation_justified: {
    label: 'Approved with Deviation',
    icon: <ShieldCheck className="w-4 h-4 text-amber-700" />,
    cls: 'bg-amber-50 border-amber-200',
  },
  mismatch: {
    label: 'Mismatch',
    icon: <XCircle className="w-4 h-4 text-red-700" />,
    cls: 'bg-red-50 border-red-200',
  },
  inconclusive: {
    label: 'Inconclusive',
    icon: <MinusCircle className="w-4 h-4 text-amber-700" />,
    cls: 'bg-amber-50 border-amber-200',
  },
  skipped: {
    label: 'N/A',
    icon: <MinusCircle className="w-4 h-4 text-slate-500" />,
    cls: 'bg-slate-50 border-slate-200',
  },
};

function VerdictHeader({
  state, finding,
}: { state: CamRowState; finding: FindingDTO | null }) {
  const meta = STATE_META[state];
  return (
    <>
      <div className={`rounded-lg border ${meta.cls} p-3 mb-3`}>
        <div className="flex items-center gap-2">
          {meta.icon}
          <span className="text-label font-semibold text-text-primary">
            {meta.label}
          </span>
          {finding?.ruleId && (
            <code className="text-micro text-text-tertiary ml-auto font-mono">
              {finding.ruleId}
            </code>
          )}
        </div>
      </div>
      {finding?.description && (
        <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 mb-4">
          <div className="flex items-start gap-2">
            <Info className="w-3.5 h-3.5 text-brand-blue flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-micro uppercase tracking-wide font-semibold text-text-tertiary mb-1">
                Why this verdict
              </div>
              <div className="text-caption text-text-secondary leading-relaxed">
                {finding.description}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mb-4">
      <div className="text-micro uppercase tracking-wide font-semibold text-text-tertiary mb-1.5">
        {title}
      </div>
      {children}
    </div>
  );
}

function EmptyHint({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-md border border-dashed border-glass-hairline bg-slate-50/60 p-3 text-caption text-text-tertiary text-center">
      {children}
    </div>
  );
}

/**
 * The core building block: a 3-column comparison table showing LOS value,
 * CAM value, and verdict (match/mismatch) per field. Used by most of the
 * 12 renderers.
 */
function ComparisonTable({
  rows,
}: {
  rows: Array<{
    field: string;
    label: string;
    losValue: ReactNode;
    camValue: ReactNode;
    match: boolean | 'partial' | null;
    pageNumber?: number | null;
    onJumpToPage?: (page: number) => void;
  }>;
}) {
  if (rows.length === 0) {
    return <EmptyHint>No comparable fields available.</EmptyHint>;
  }
  return (
    <div className="overflow-x-auto">
    <table className="w-full text-caption">
      <thead>
        <tr className="text-text-tertiary border-b border-glass-hairline">
          <th className="text-left font-medium pb-1.5">Field</th>
          <th className="text-left font-medium pb-1.5">LOS</th>
          <th className="text-left font-medium pb-1.5">CAM</th>
          <th className="text-right font-medium pb-1.5">Verdict</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.field} className="border-b border-glass-hairline last:border-0">
            <td className="py-1.5 align-top">
              <div className="font-medium text-text-primary">{r.label}</div>
              {r.pageNumber && r.onJumpToPage && (
                <button
                  type="button"
                  onClick={() => r.onJumpToPage!(r.pageNumber!)}
                  className="text-micro text-brand-blue mt-0.5 hover:underline"
                  title={`Jump CAM PDF to page ${r.pageNumber}`}
                >
                  → p.{r.pageNumber}
                </button>
              )}
            </td>
            <td className="py-1.5 align-top font-mono text-micro text-text-primary">
              {r.losValue ?? <span className="text-text-tertiary">—</span>}
            </td>
            <td className="py-1.5 align-top font-mono text-micro text-text-primary">
              {r.camValue ?? <span className="text-text-tertiary">—</span>}
            </td>
            <td className="py-1.5 align-top text-right">
              {r.match === true ? (
                <Badge tone="success">✓</Badge>
              ) : r.match === false ? (
                <Badge tone="danger">✗</Badge>
              ) : r.match === 'partial' ? (
                <Badge tone="warning">~</Badge>
              ) : (
                <span className="text-text-tertiary">—</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    </div>
  );
}

function NoDataFallback({ what }: { what: string }) {
  return (
    <EmptyHint>
      {what} data not available. Re-upload CAM/LOS and re-run validation
      to populate this evidence.
    </EmptyHint>
  );
}

// =========================================================================
// Helpers for formatting
// =========================================================================

function fmtInr(v: number | null | undefined): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  return `₹${v.toLocaleString('en-IN')}`;
}
function fmtPct(v: number | null | undefined): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  return `${v}%`;
}
function fmtDate(v: string | null | undefined): string {
  return v ?? '—';
}
function fmtBool(v: boolean | null | undefined): string {
  if (v === true) return 'Yes';
  if (v === false) return 'No';
  return '—';
}
function normaliseText(s: string | null | undefined): string {
  if (!s) return '';
  return s.toLowerCase().replace(/\s+/g, ' ').replace(/[.,;:\-_]+$/g, '').trim();
}

// =========================================================================
// MAIN COMPONENT
// =========================================================================

export function CamEvidenceDrawer(props: CamEvidenceDrawerProps) {
  const { open, onClose, point, label } = props;
  const [camPage, setCamPage] = useState<number | null>(null);

  return (
    <RadixDialog.Root open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm animate-fade-in" />
        <RadixDialog.Content
          className={[
            'fixed z-50 inset-3 md:inset-6 lg:inset-8',
            'liquid-glass-strong rounded-glass-xl',
            'flex flex-col overflow-hidden animate-fade-in',
          ].join(' ')}
        >
          {/* ---- Header ---- */}
          <div className="shrink-0 flex items-center gap-3 px-5 py-3.5 border-b border-glass-hairline bg-white/60">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-1.5 text-label font-medium text-brand-blue hover:text-brand-blue/80 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Checklist
            </button>
            <div className="w-px h-5 bg-glass-hairline" />
            <RadixDialog.Title className="text-heading font-semibold text-text-primary tracking-tight truncate">
              {point ? `Point ${point}: ${label}` : 'Evidence'}
            </RadixDialog.Title>
            <div className="flex-1" />
            <RadixDialog.Close
              aria-label="Close"
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-900/5 hover:bg-slate-900/10 shrink-0"
            >
              <X className="w-4 h-4 text-text-secondary" />
            </RadixDialog.Close>
          </div>

          {/* ---- Body ---- */}
          <div className="flex-1 overflow-y-auto scrollbar-subtle px-6 py-5">
            <div className="max-w-5xl mx-auto">
              <VerdictHeader
                state={props.state}
                finding={props.finding}
              />
              {point && renderForPoint(point, props, camPage, setCamPage)}
            </div>
          </div>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

// =========================================================================
// Dispatcher
// =========================================================================

function renderForPoint(
  point: number,
  props: CamEvidenceDrawerProps,
  camPage: number | null,
  setCamPage: (n: number | null) => void,
): ReactNode {
  switch (point) {
    case 1:  return <DocumentsRequiredEvidence {...props} />;
    case 2:  return <CibilScoreEvidence {...props} />;
    case 3:  return <IncomeProofPlBlEvidence {...props} />;
    case 5:  return <NovelCheckEvidence {...props} />;
    case 6:  return <LoanEligibilityEvidence {...props} camPage={camPage} setCamPage={setCamPage} />;
    case 7:  return <PolicyLevelEvidence {...props} />;
    case 8:  return <DeviationMatrixEvidence {...props} />;
    case 9:  return <SanctionConditionsEvidence {...props} />;
    case 10: return <LegalDetailEvidence {...props} />;
    case 11: return <ValuationReportEvidence {...props} />;
    case 12: return <PersonalDiscussionEvidence {...props} />;
    default: return <EmptyHint>No renderer for point {point}.</EmptyHint>;
  }
}

// =========================================================================
// Point 1 — Documents Required
// Five sections matching the five rule sub-checks:
//   A. CAM letter present
//   B. CFA Excel present
//   C. CAM-referenced documents
//   D. Per-borrower KYC
//   E. Product-wise mandatory documents (from masters)
// =========================================================================

function DocumentsRequiredEvidence(props: CamEvidenceDrawerProps) {
  const cam = getCamData(props.parsingView, props.documents);
  if (!cam.fields) return <NoDataFallback what="CAM" />;

  const extra = (props.finding?.evidence as any)?.extra ?? {};

  const presentDocTypes = new Set(props.documents.map((d) => d.documentType));

  // ── A: CAM letter ───────────────────────────────────────────────────────
  // Prefer rule-computed value; fall back to local check
  const camLetterPresent: boolean = extra.camLetterPresent ?? presentDocTypes.has('cam');

  // ── B: CFA Excel ────────────────────────────────────────────────────────
  const cfaExcelPresent: boolean = extra.cfaExcelPresent ?? presentDocTypes.has('cfa_excel');

  // ── C: CAM-referenced documents ─────────────────────────────────────────
  const claimed: string[] = extra.claimedDocs
    ?? (getCamField<string[]>(cam.fields, 'documents_referenced') ?? []).map((d: string) => d.toLowerCase().trim());
  const missingClaimed: string[] = extra.missingClaimedDocs ?? [];
  const resolveType = (dt: string): string => {
    const aliases: Record<string, string> = {
      legal_report: 'legal_technical_report', legal: 'legal_technical_report',
      valuation_report: 'valuation', property_valuation: 'valuation',
      salary: 'salary_slip', payslip: 'salary_slip', pay_slip: 'salary_slip',
      cibil: 'cibil_report', credit_report: 'cibil_report',
      bank_stmt: 'bank_statement', bs: 'bank_statement',
      cfa: 'cfa_excel', cfa_sheet: 'cfa_excel', credit_facility_appraisal: 'cfa_excel',
    };
    return aliases[dt.toLowerCase()] ?? dt;
  };

  // ── D: Per-borrower KYC ─────────────────────────────────────────────────
  type ApplicantKyc = { name: string; kycListed: number; kycRequired: number; ok: boolean };
  const perApplicantKyc: ApplicantKyc[] = extra.perApplicantKyc ?? [];
  const totalKycDocs: number = extra.totalKycDocs
    ?? props.documents.filter((d) => ['kyc', 'kyc_report', 'aadhaar', 'pan', 'passport', 'voter_id', 'driving_licence'].includes(d.documentType)).length;
  const minKycPerApplicant: number = extra.minKycPerApplicant ?? 2;

  // Fall back to reading CAM applicants directly when rule evidence is absent
  const camApplicants = perApplicantKyc.length > 0
    ? perApplicantKyc
    : (getCamField<Array<{ name: string; kyc_documents_listed?: string[] }>>(cam.fields, 'applicants') ?? []).map(
        (a) => ({
          name: a.name,
          kycListed: a.kyc_documents_listed?.length ?? 0,
          kycRequired: minKycPerApplicant,
          ok: (a.kyc_documents_listed?.length ?? 0) >= minKycPerApplicant,
        }),
      );

  // ── E: Product mandatory checklist ─────────────────────────────────────
  type ChecklistRow = { docType: string; present: boolean; severity?: string };
  const mandatoryDocChecklist: ChecklistRow[] = extra.mandatoryDocChecklist ?? [];

  return (
    <>
      {/* A + B — Required base documents */}
      <Section title="A — Required base documents">
        <table className="w-full text-caption">
          <thead>
            <tr className="text-text-tertiary border-b border-glass-hairline">
              <th className="text-left font-medium pb-1.5">Document</th>
              <th className="text-right font-medium pb-1.5">Status</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-glass-hairline">
              <td className="py-1.5 text-text-primary font-medium">CAM Letter</td>
              <td className="py-1.5 text-right">
                {camLetterPresent
                  ? <Badge tone="success">✓ Present</Badge>
                  : <Badge tone="danger">✗ Missing</Badge>}
              </td>
            </tr>
            <tr className="border-b border-glass-hairline last:border-0">
              <td className="py-1.5 text-text-primary font-medium">CFA Excel Sheet</td>
              <td className="py-1.5 text-right">
                {cfaExcelPresent
                  ? <Badge tone="success">✓ Present</Badge>
                  : <Badge tone="danger">✗ Missing</Badge>}
              </td>
            </tr>
          </tbody>
        </table>
      </Section>

      {/* C — CAM-referenced documents */}
      <Section title="B — Documents referenced in CAM">
        <table className="w-full text-caption">
          <thead>
            <tr className="text-text-tertiary border-b border-glass-hairline">
              <th className="text-left font-medium pb-1.5">Document type</th>
              <th className="text-right font-medium pb-1.5">Present in LOS?</th>
            </tr>
          </thead>
          <tbody>
            {claimed.length === 0 ? (
              <tr>
                <td colSpan={2} className="py-2 text-text-tertiary italic">
                  CAM didn't reference any documents explicitly.
                </td>
              </tr>
            ) : claimed.map((dt) => {
              const isPresent = missingClaimed.length > 0
                ? !missingClaimed.includes(dt)
                : presentDocTypes.has(resolveType(dt));
              return (
                <tr key={dt} className="border-b border-glass-hairline last:border-0">
                  <td className="py-1.5 text-text-primary">{formatDocTypeName(dt)}</td>
                  <td className="py-1.5 text-right">
                    {isPresent
                      ? <Badge tone="success">✓ Present</Badge>
                      : <Badge tone="danger">✗ Missing</Badge>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Section>

      {/* D — Per-borrower KYC */}
      <Section title="C — Borrower KYC verification">
        <table className="w-full text-caption">
          <thead>
            <tr className="text-text-tertiary border-b border-glass-hairline">
              <th className="text-left font-medium pb-1.5">Applicant (from CAM)</th>
              <th className="text-right font-medium pb-1.5">KYC docs listed</th>
            </tr>
          </thead>
          <tbody>
            {camApplicants.length === 0 ? (
              <tr>
                <td colSpan={2} className="py-2 text-text-tertiary italic">No applicants found in CAM.</td>
              </tr>
            ) : camApplicants.map((a, i) => (
              <tr key={i} className="border-b border-glass-hairline last:border-0">
                <td className="py-1.5 text-text-primary">{a.name}</td>
                <td className="py-1.5 text-right">
                  <span className={a.ok ? 'text-emerald-700 font-medium' : 'text-red-700 font-medium'}>
                    {a.kycListed}/{a.kycRequired} {a.ok ? '✓' : '✗'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="text-micro text-text-tertiary mt-1.5">
          Total KYC-type docs uploaded: <span className="font-semibold">{totalKycDocs}</span>
          {' '}· Required: <span className="font-semibold">{minKycPerApplicant} per borrower</span>
        </div>
      </Section>

      {/* E — Product mandatory checklist */}
      <Section title="D — Product-wise mandatory documents (Masters)">
        {mandatoryDocChecklist.length === 0 ? (
          <EmptyHint>
            No mandatory document checklist configured for this product yet.
            Add records to the <strong>Document Checklist</strong> master type
            in Masters → document_checklist to enable this check.
          </EmptyHint>
        ) : (
          <table className="w-full text-caption">
            <thead>
              <tr className="text-text-tertiary border-b border-glass-hairline">
                <th className="text-left font-medium pb-1.5">Mandatory document</th>
                <th className="text-right font-medium pb-1.5">Status</th>
              </tr>
            </thead>
            <tbody>
              {mandatoryDocChecklist.map((row: ChecklistRow) => (
                <tr key={row.docType} className="border-b border-glass-hairline last:border-0">
                  <td className="py-1.5 text-text-primary">{formatDocTypeName(row.docType)}</td>
                  <td className="py-1.5 text-right">
                    {row.present
                      ? <Badge tone="success">✓ Present</Badge>
                      : <Badge tone="danger">✗ Missing</Badge>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="text-micro text-text-tertiary mt-1.5">
          Source: <span className="font-medium">Masters → Document Checklist</span> (admin-managed, no code change needed to update)
        </div>
      </Section>
    </>
  );
}

// =========================================================================
// Point 2 — CIBIL Score
// =========================================================================

function CibilScoreEvidence({ losData, parsingView, documents }: CamEvidenceDrawerProps) {
  const cam = getCamData(parsingView, documents);

  // Source 1: LOS
  const losScore = (losData?.data?.applicants?.[0] as any)?.cibilScore as number | undefined ?? null;

  // Source 2: CAM — try multiple extraction paths
  let camScore: number | null = null;
  if (cam.fields) {
    // Path 1: top-level cibil_score field
    camScore = getCamField<number>(cam.fields, 'cibil_score') ?? null;
    // Path 2: nested inside applicants array
    if (camScore == null) {
      const applicants = getCamField<Array<Record<string, unknown>>>(cam.fields, 'applicants');
      if (Array.isArray(applicants) && applicants.length > 0) {
        const first = applicants[0] as Record<string, unknown>;
        const nested = first?.cibil_score;
        if (typeof nested === 'number') camScore = nested;
        else if (typeof nested === 'object' && nested != null && 'value' in nested) {
          camScore = (nested as { value: number }).value ?? null;
        }
      }
    }
  }

  // Source 3: CIBIL Report PDF
  const cibilFields = getDocFields(parsingView, 'cibil_report');
  const cibilReportScore = getDocField<number>(cibilFields, 'cibil_score');

  // Product threshold — fetch directly from CIBIL Thresholds masters
  const productCode = losData?.data?.productCode ?? '';
  const cibilThresholdQuery = useQuery({
    queryKey: ['cibilThreshold', productCode],
    queryFn: async () => {
      if (!productCode) return null;
      try {
        const typeDetail = await mastersApi.getType('cibil_thresholds');
        const rec = typeDetail.records.find((r: any) => r.recordKey === productCode);
        if (!rec) return null;
        const detail = await mastersApi.getRecord(rec.id);
        return detail.content as { minScore?: number; operator?: 'gte' | 'lte' };
      } catch { return null; }
    },
    enabled: !!productCode,
    staleTime: 30_000,
  });
  const minThreshold: number | null = (cibilThresholdQuery.data as any)?.minScore ?? null;
  const thresholdOperator: 'gte' | 'lte' = (cibilThresholdQuery.data as any)?.operator ?? 'gte';
  const effectiveScore = losScore ?? camScore ?? cibilReportScore ?? null;

  // 3-way match
  const defined = [
    { src: 'LOS', val: losScore },
    { src: 'CAM', val: camScore },
    { src: 'CIBIL Report', val: cibilReportScore },
  ].filter((s) => s.val != null);
  const allMatch = defined.length >= 2 ? defined.every((s) => s.val === defined[0]!.val) : null;
  const meetsThreshold = effectiveScore != null && minThreshold != null
    ? (thresholdOperator === 'lte' ? effectiveScore <= minThreshold : effectiveScore >= minThreshold)
    : null;

  return (
    <>
      <Section title="CIBIL Score — 3-way comparison">
        <div className="overflow-x-auto">
        <table className="w-full text-caption">
          <thead>
            <tr className="text-text-tertiary border-b border-glass-hairline text-micro">
              <th className="text-left font-medium pb-1.5 w-[25%]">Source</th>
              <th className="text-left font-medium pb-1.5 w-[25%]">CIBIL Score</th>
              <th className="text-left font-medium pb-1.5 w-[25%]">From</th>
              <th className="text-right font-medium pb-1.5 w-[25%]">Match</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-glass-hairline">
              <td className="py-1.5 font-medium text-text-secondary">LOS</td>
              <td className="py-1.5 font-semibold text-label">{losScore ?? '—'}</td>
              <td className="py-1.5 text-text-tertiary">applicants[0].cibilScore</td>
              <td className="py-1.5 text-right">
                {allMatch === true ? <Badge tone="success">✓</Badge>
                  : allMatch === false ? <Badge tone="danger">✗</Badge>
                  : <span className="text-text-tertiary">—</span>}
              </td>
            </tr>
            <tr className="border-b border-glass-hairline">
              <td className="py-1.5 font-medium text-text-secondary">CAM</td>
              <td className="py-1.5 font-semibold text-label">{camScore ?? '—'}</td>
              <td className="py-1.5 text-text-tertiary">CAM extraction</td>
              <td className="py-1.5 text-right">
                {allMatch === true ? <Badge tone="success">✓</Badge>
                  : allMatch === false ? <Badge tone="danger">✗</Badge>
                  : <span className="text-text-tertiary">—</span>}
              </td>
            </tr>
            <tr className="border-b border-glass-hairline">
              <td className="py-1.5 font-medium text-text-secondary">
                <span className="inline-flex items-center gap-1">
                  CIBIL Report
                  <span className="text-micro font-normal text-brand-blue bg-brand-blue/[0.06] px-1 rounded">PDF</span>
                </span>
              </td>
              <td className="py-1.5 font-semibold text-label">{cibilReportScore ?? '—'}</td>
              <td className="py-1.5 text-text-tertiary">CIBIL Report extraction</td>
              <td className="py-1.5 text-right">
                {allMatch === true ? <Badge tone="success">✓</Badge>
                  : allMatch === false ? <Badge tone="danger">✗</Badge>
                  : <span className="text-text-tertiary">—</span>}
              </td>
            </tr>
          </tbody>
        </table>
        </div>
      </Section>

      <Section title="Product threshold check">
        <div className="overflow-x-auto">
        <table className="w-full text-caption">
          <thead>
            <tr className="text-text-tertiary border-b border-glass-hairline text-micro">
              <th className="text-left font-medium pb-1.5 w-[35%]">Parameter</th>
              <th className="text-left font-medium pb-1.5 w-[35%]">Value</th>
              <th className="text-left font-medium pb-1.5 w-[20%]">Source</th>
              <th className="text-right font-medium pb-1.5 w-[10%]">Verdict</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-glass-hairline">
              <td className="py-1.5 font-medium text-text-secondary">Product</td>
              <td className="py-1.5">{productCode}</td>
              <td className="py-1.5 text-text-tertiary">LOS</td>
              <td />
            </tr>
            <tr className="border-b border-glass-hairline">
              <td className="py-1.5 font-medium text-text-secondary">
                {thresholdOperator === 'lte' ? 'Max CIBIL allowed' : 'Min CIBIL required'}
              </td>
              <td className="py-1.5 font-semibold">
                {minThreshold != null ? (thresholdOperator === 'lte' ? `≤ ${minThreshold}` : `≥ ${minThreshold}`) : '—'}
              </td>
              <td className="py-1.5 text-text-tertiary">Masters (CIBIL Thresholds)</td>
              <td />
            </tr>
            <tr className={"border-b border-glass-hairline " + (meetsThreshold === false ? 'bg-red-50/40' : meetsThreshold === true ? 'bg-emerald-50/30' : '')}>
              <td className="py-1.5 font-medium text-text-secondary">Applicant score</td>
              <td className="py-1.5 font-semibold text-label">
                {effectiveScore ?? '—'}
                {effectiveScore != null && minThreshold != null && (
                  <span className="text-text-tertiary text-micro ml-2">
                    {thresholdOperator === 'lte'
                      ? `(${effectiveScore} ${effectiveScore <= minThreshold ? '≤' : '>'} ${minThreshold})`
                      : `(${effectiveScore} ${effectiveScore >= minThreshold ? '≥' : '<'} ${minThreshold})`}
                  </span>
                )}
              </td>
              <td className="py-1.5 text-text-tertiary">LOS / CAM / Report</td>
              <td className="py-1.5 text-right">
                {meetsThreshold === true ? <Badge tone="success">✓ Meets</Badge>
                  : meetsThreshold === false
                    ? <Badge tone="danger">{thresholdOperator === 'lte' ? '✗ Above' : '✗ Below'}</Badge>
                  : <span className="text-text-tertiary">—</span>}
              </td>
            </tr>
          </tbody>
        </table>
        </div>
      </Section>
    </>
  );
}

function IncomeProofPlBlEvidence({ losData, parsingView, documents, finding }: CamEvidenceDrawerProps) {
  const cam = getCamData(parsingView, documents);
  if (!cam.fields || !losData) return <NoDataFallback what={!cam.fields ? 'CAM' : 'LOS'} />;

  const ps = losData.data.payslipVerification ?? null;
  const losNc = losData.data.novelCheckBanking;
  const camNetIncome = getCamField<number>(cam.fields, 'net_monthly_income_inr') ?? getCamField<number>(cam.fields, 'combined_net_income_inr');
  const camGrossIncome = getCamField<number>(cam.fields, 'gross_monthly_income_inr');
  const camEmployer = getCamField<string>(cam.fields, 'employer_name') ?? getCamField<string>(cam.fields, 'company_name');
  const camNovelCheck = getCamField<boolean>(cam.fields, 'novel_check_referenced');

  // Bank statement extracted fields (direct from PDF extraction)
  const bsFields = getDocFields(parsingView, 'bank_statement');
  const bsAvgCredit = getDocField<number>(bsFields, 'average_monthly_credit_inr');
  const bsSalaryCount = getDocField<number>(bsFields, 'salary_credits_count');
  const bsEmployer = getDocField<string>(bsFields, 'salary_employer_narration');
  const bsAvgBalance = getDocField<number>(bsFields, 'average_monthly_balance_inr');

  const extra = (finding?.evidence as any)?.extra ?? {};
  const bvEvidence = extra.bankingVerification ?? null;
  const salaryVsBankDiffPct = extra.salaryVsBankDiffPct ?? null;

  // Payslip math
  let totalDeductions: number | null = null;
  let calcNet: number | null = null;
  if (ps) {
    totalDeductions = (ps.pfDeduction ?? 0) + (ps.incomeTaxDeduction ?? 0)
      + (ps.professionalTax ?? 0) + (ps.otherDeductions ?? 0);
    calcNet = ps.grossMonthlySalary - totalDeductions;
  }

  // Net salary match check across all 4 sources
  const slipNet = ps?.netMonthlySalary ?? null;
  const losNet = ps?.netMonthlySalary ?? null; // same source for now
  const camNet = camNetIncome ?? null;
  // Bank net: prefer banking verification if available, fall back to bank statement extraction
  const bankNet = bvEvidence?.observedSalaryAvgInr ?? bsAvgCredit ?? null;

  // Pre-compute net salary match with 10% tolerance for bank
  let netSalaryMatch: boolean | null = null;
  if (slipNet != null && camNet != null && bankNet != null) {
    const bankDiff = Math.abs(Number(bankNet) - Number(slipNet)) / Number(slipNet);
    netSalaryMatch = Number(slipNet) === Number(camNet) && bankDiff <= 0.10;
  } else if (slipNet != null && camNet != null) {
    netSalaryMatch = Number(slipNet) === Number(camNet);
  }

  return (
    <>
      {/* Unified 4-source comparison table */}
      <Section title="Income verification — all sources">
        <div className="overflow-x-auto rounded-md border border-glass-hairline">
          <table className="w-full text-caption">
            <thead>
              <tr className="bg-slate-50/80 border-b border-glass-hairline text-micro text-text-tertiary font-semibold">
                <th className="px-3 py-2 text-left">Field</th>
                <th className="px-3 py-2 text-right">Salary Slip</th>
                <th className="px-3 py-2 text-right">LOS</th>
                <th className="px-3 py-2 text-right">CAM</th>
                <th className="px-3 py-2 text-right">Bank Stmt</th>
                <th className="px-3 py-2 text-center">Match</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-glass-hairline">
              <FourWayRow
                label="Gross salary"
                slip={fmtInr(ps?.grossMonthlySalary)}
                los={fmtInr(ps?.grossMonthlySalary)}
                cam={fmtInr(camGrossIncome)}
                bank="—"
                match={ps && camGrossIncome ? ps.grossMonthlySalary === camGrossIncome : null}
              />
              <FourWayRow label="PF deduction" slip={fmtInr(ps?.pfDeduction)} los={fmtInr(ps?.pfDeduction)} cam="—" bank="—" match={null} />
              <FourWayRow label="Income tax" slip={fmtInr(ps?.incomeTaxDeduction)} los={fmtInr(ps?.incomeTaxDeduction)} cam="—" bank="—" match={null} />
              <FourWayRow label="Professional tax" slip={fmtInr(ps?.professionalTax)} los={fmtInr(ps?.professionalTax)} cam="—" bank="—" match={null} />
              <FourWayRow label="Other deductions" slip={fmtInr(ps?.otherDeductions)} los={fmtInr(ps?.otherDeductions)} cam="—" bank="—" match={null} />
              <FourWayRow
                label="Net salary"
                slip={fmtInr(slipNet)}
                los={fmtInr(losNet)}
                cam={fmtInr(camNet)}
                bank={fmtInr(bankNet)}
                match={netSalaryMatch}
                highlight
              />
              <FourWayRow
                label="Employer"
                slip={ps?.employerNameOnPayslip ?? '—'}
                los={losData.data.companyCategorization?.employerName ?? '—'}
                cam={camEmployer ?? '—'}
                bank={bvEvidence ? (bvEvidence.salaryEmployerMatch ? '✓ Matched' : '✗ Mismatch') : (bsEmployer ?? '—')}
                match={
                  ps && camEmployer
                    ? normaliseText(ps.employerNameOnPayslip) === normaliseText(camEmployer)
                    : null
                }
              />
              <FourWayRow
                label="Salary entries"
                slip={ps ? String(ps.monthsCovered) : '—'}
                los={String(losNc?.salaryEntries ?? '—')}
                cam="—"
                bank={bvEvidence ? String(bvEvidence.salaryCreditsFound) : (bsSalaryCount != null ? String(bsSalaryCount) : '—')}
                match={null}
              />
              <FourWayRow
                label="Monthly avg balance"
                slip="—"
                los={fmtInr(losNc?.monthlyAverageBalance)}
                cam="—"
                bank={bvEvidence ? fmtInr(bvEvidence.observedSalaryAvgInr) : fmtInr(bsAvgBalance)}
                match={null}
              />
            </tbody>
          </table>
        </div>
      </Section>

      {/* Payslip math check */}
      <Section title="Payslip math check">
        {ps ? (
          <div className="rounded-md border border-glass-hairline bg-slate-50/60 p-2.5">
            <div className="text-caption space-y-0.5">
              <div>Gross: <span className="font-medium">{fmtInr(ps.grossMonthlySalary)}</span></div>
              <div className="text-text-tertiary">
                − PF: {fmtInr(ps.pfDeduction ?? 0)}
                {' '}− IT: {fmtInr(ps.incomeTaxDeduction ?? 0)}
                {' '}− PT: {fmtInr(ps.professionalTax ?? 0)}
                {' '}− Other: {fmtInr(ps.otherDeductions ?? 0)}
                {' '}= Total deductions: {fmtInr(totalDeductions)}
              </div>
              <div>Calculated net: <span className="font-medium">{fmtInr(calcNet)}</span></div>
              <div>
                Declared net: <span className="font-medium">{fmtInr(ps.netMonthlySalary)}</span>
                {calcNet !== null && (
                  <span className={`ml-2 font-semibold ${Math.abs(calcNet - ps.netMonthlySalary) <= 100 ? 'text-emerald-700' : 'text-red-700'}`}>
                    {Math.abs(calcNet - ps.netMonthlySalary) <= 100 ? '✓ Math checks out' : `✗ Diff ₹${Math.abs(calcNet - ps.netMonthlySalary).toLocaleString('en-IN')}`}
                  </span>
                )}
              </div>
            </div>
          </div>
        ) : (
          <EmptyHint>Add <code className="font-mono text-micro bg-slate-100 px-1 rounded">payslipVerification</code> to LOS JSON for payslip math check.</EmptyHint>
        )}
      </Section>

      {/* Net salary cross-match */}
      {slipNet && bankNet && (
        <Section title="Net salary vs bank credits">
          <div className={`rounded-md border p-2.5 text-caption ${
            Math.abs(bankNet - slipNet) / slipNet <= 0.10
              ? 'border-emerald-200 bg-emerald-50/40 text-emerald-800'
              : 'border-red-200 bg-red-50/40 text-red-800'
          }`}>
            Payslip net: {fmtInr(slipNet)} · Bank avg credit: {fmtInr(bankNet)} · Difference: {salaryVsBankDiffPct !== null ? `${(salaryVsBankDiffPct * 100).toFixed(1)}%` : `${((Math.abs(bankNet - slipNet) / slipNet) * 100).toFixed(1)}%`} {Math.abs(bankNet - slipNet) / slipNet <= 0.10 ? '✓ Within 10% tolerance' : '✗ Exceeds 10% tolerance'}
          </div>
        </Section>
      )}

      {/* Tamper detection */}
      <Section title="Edited PDF detection">
        {bvEvidence ? (
          <div className={`rounded-md border p-2.5 text-caption ${bvEvidence.editedPdfDetected ? 'border-red-200 bg-red-50/40 text-red-800' : 'border-emerald-200 bg-emerald-50/40 text-emerald-800'}`}>
            Bank statement: {bvEvidence.editedPdfDetected ? '⚠ PDF editing detected — CRITICAL' : '✓ No editing detected — clean'}
          </div>
        ) : (
          <EmptyHint>Run banking verification to check for PDF tampering.</EmptyHint>
        )}
      </Section>

      {/* Novel Check status */}
      <Section title="Novel Check status">
        <div className="grid grid-cols-2 gap-2 text-caption">
          <div className="rounded-md border border-glass-hairline bg-slate-50/50 p-2">
            <div className="text-micro text-text-tertiary uppercase font-semibold">LOS</div>
            <div className={losNc?.performed ? 'text-emerald-700' : 'text-red-700'}>
              {losNc?.performed ? '✓ Performed' : '✗ Not performed'}
            </div>
          </div>
          <div className="rounded-md border border-glass-hairline bg-slate-50/50 p-2">
            <div className="text-micro text-text-tertiary uppercase font-semibold">CAM</div>
            <div className={camNovelCheck ? 'text-emerald-700' : 'text-red-700'}>
              {camNovelCheck ? '✓ Referenced' : '✗ Not referenced'}
            </div>
          </div>
        </div>
      </Section>
    </>
  );
}

function FourWayRow({ label, slip, los, cam, bank, match, highlight }: {
  label: string; slip: string; los: string; cam: string; bank: string;
  match: boolean | null; highlight?: boolean;
}) {
  return (
    <tr className={highlight ? 'bg-brand-blue/[0.04] font-medium' : 'hover:bg-slate-50/40'}>
      <td className="px-3 py-2 text-text-secondary">{label}</td>
      <td className="px-3 py-2 text-right tabular-nums">{slip}</td>
      <td className="px-3 py-2 text-right tabular-nums">{los}</td>
      <td className="px-3 py-2 text-right tabular-nums">{cam}</td>
      <td className="px-3 py-2 text-right tabular-nums">{bank}</td>
      <td className="px-3 py-2 text-center">
        {match === null ? <span className="text-text-tertiary">—</span>
          : match ? <span className="text-emerald-600 font-semibold">✓</span>
          : <span className="text-red-600 font-semibold">✗</span>}
      </td>
    </tr>
  );
}

// =========================================================================
// Point 5 — Novel Check
// =========================================================================

function NovelCheckEvidence({ losData, parsingView, documents }: CamEvidenceDrawerProps) {
  const cam = getCamData(parsingView, documents);
  if (!cam.fields || !losData) return <NoDataFallback what={!cam.fields ? 'CAM' : 'LOS'} />;

  const camRef = getCamField<boolean>(cam.fields, 'novel_check_referenced');
  const camMethod = getCamField<string>(cam.fields, 'income_assessment_method');
  const camAvgBalance = getCamField<number>(cam.fields, 'banking_avg_balance_inr');
  const camGstTurnover = getCamField<number>(cam.fields, 'gst_turnover_inr_annual');
  const losNc = losData.data.novelCheckBanking;

  return (
    <>
      <Section title="Novel Check verification">
        <ComparisonTable rows={[
          {
            field: 'performed',
            label: 'Novel Check performed',
            losValue: fmtBool(losNc?.performed),
            camValue: fmtBool(camRef),
            match: losNc?.performed === true && camRef === true,
          },
          {
            field: 'salary_verified',
            label: 'Salary credits verified',
            losValue: fmtBool(losNc?.salaryCreditsVerified),
            camValue: '—',
            match: null,
          },
        ]} />
      </Section>
      <Section title={`Method-specific data (CAM says: ${camMethod ?? 'unknown'})`}>
        <ComparisonTable rows={[
          {
            field: 'avg_balance',
            label: 'Banking avg balance',
            losValue: fmtInr(losNc?.monthlyAverageBalance ?? null),
            camValue: fmtInr(camAvgBalance),
            match: camAvgBalance != null && camAvgBalance > 0,
          },
          {
            field: 'gst_turnover',
            label: 'GST annual turnover',
            losValue: '—',
            camValue: fmtInr(camGstTurnover),
            match: camMethod === 'gst_program'
              ? (camGstTurnover != null && camGstTurnover > 0)
              : null,
          },
        ]} />
      </Section>
    </>
  );
}

// =========================================================================
// Point 6 — Loan Eligibility (the big one)
// =========================================================================

function LoanEligibilityEvidence({
  losData, parsingView, documents, loanFileId, camPage, setCamPage,
}: CamEvidenceDrawerProps & { camPage: number | null; setCamPage: (n: number | null) => void }) {
  const cam = getCamData(parsingView, documents);
  if (!cam.fields || !losData?.data?.loanDetails) {
    return <NoDataFallback what={!cam.fields ? 'CAM' : 'LOS'} />;
  }
  const ld = losData.data.loanDetails;

  const camAmount = getCamField<number>(cam.fields, 'sanctioned_amount_inr');
  const camRate = getCamField<number>(cam.fields, 'interest_rate_pct');
  const camTenure = getCamField<number>(cam.fields, 'tenure_months');
  const camEmi = getCamField<number>(cam.fields, 'emi_amount_inr');
  const camFoir = getCamField<number>(cam.fields, 'foir_pct');
  const camLtv = getCamField<number>(cam.fields, 'ltv_pct');
  const camPurpose = getCamField<string>(cam.fields, 'loan_purpose');

  const losPurpose = (ld as { loanPurpose?: string | null }).loanPurpose ?? null;

  const rows = [
    {
      field: 'sanctioned_amount',
      label: 'Sanctioned amount',
      losValue: fmtInr(ld.sanctionedAmount),
      camValue: fmtInr(camAmount),
      match: camAmount != null && ld.sanctionedAmount != null
        ? Math.abs(ld.sanctionedAmount - camAmount) <= 100 : null,
      pageNumber: getCamPage(cam.fields, 'sanctioned_amount_inr'),
      onJumpToPage: setCamPage,
    },
    {
      field: 'interest_rate',
      label: 'Interest rate',
      losValue: fmtPct(ld.interestRatePct),
      camValue: fmtPct(camRate),
      match: camRate != null && ld.interestRatePct != null
        ? Math.abs(ld.interestRatePct - camRate) <= 0.01 : null,
      pageNumber: getCamPage(cam.fields, 'interest_rate_pct'),
      onJumpToPage: setCamPage,
    },
    {
      field: 'tenure',
      label: 'Tenure (months)',
      losValue: ld.tenureMonths != null ? String(ld.tenureMonths) : '—',
      camValue: camTenure !== null ? String(camTenure) : '—',
      match: camTenure != null && ld.tenureMonths != null
        ? ld.tenureMonths === camTenure : null,
      pageNumber: getCamPage(cam.fields, 'tenure_months'),
      onJumpToPage: setCamPage,
    },
    {
      field: 'emi',
      label: 'EMI',
      losValue: fmtInr(ld.emiAmount),
      camValue: fmtInr(camEmi),
      match: camEmi != null && ld.emiAmount != null
        ? Math.abs(ld.emiAmount - camEmi) <= 10 : null,
      pageNumber: getCamPage(cam.fields, 'emi_amount_inr'),
      onJumpToPage: setCamPage,
    },
    {
      field: 'foir',
      label: 'FOIR %',
      losValue: fmtPct((ld as { foirPct?: number | null }).foirPct ?? null),
      camValue: fmtPct(camFoir),
      match: (ld as { foirPct?: number | null }).foirPct != null && camFoir != null
        ? (ld as { foirPct?: number | null }).foirPct === camFoir : null,
      pageNumber: getCamPage(cam.fields, 'foir_pct'),
      onJumpToPage: setCamPage,
    },
    {
      field: 'ltv',
      label: 'LTV %',
      losValue: fmtPct((ld as { ltvPct?: number | null }).ltvPct ?? null),
      camValue: fmtPct(camLtv),
      match: (ld as { ltvPct?: number | null }).ltvPct != null && camLtv != null
        ? (ld as { ltvPct?: number | null }).ltvPct === camLtv : null,
      pageNumber: getCamPage(cam.fields, 'ltv_pct'),
      onJumpToPage: setCamPage,
    },
    {
      field: 'purpose',
      label: 'Loan purpose',
      losValue: losPurpose ?? '—',
      camValue: camPurpose ?? '—',
      match: losPurpose !== null && camPurpose !== null
        && normaliseText(losPurpose) === normaliseText(camPurpose),
    },
  ];

  return (
    <>
      <Section title="LOS vs CAM — loan terms">
        <ComparisonTable rows={rows} />
        <div className="text-micro text-text-tertiary mt-2">
          Tolerances: amount ±₹100 · rate ±0.01% · tenure ±0 months · EMI ±₹10
        </div>
      </Section>

      {cam.documentId && (
        <Section title="CAM PDF (source of truth)">
          <PdfEmbed
            loanFileId={loanFileId}
            documentId={cam.documentId}
            fileName={cam.fileName ?? 'CAM.pdf'}
            initialPage={camPage}
            heightPx={420}
          />
        </Section>
      )}
    </>
  );
}

// =========================================================================
// Point 7 — Policy Level Checks
// =========================================================================

function LoanAmountCapCheck({
  losData,
  parsingView,
}: Pick<CamEvidenceDrawerProps, 'losData' | 'parsingView'>) {
  if (!losData) return null;

  const company = losData.data.companyCategorization;
  const sanctioned = losData.data.loanDetails?.sanctionedAmount ?? 0;
  const category = company?.category ?? '—';
  const maxCap = company?.maxLoanCapForCategory ?? null;

  // CIBIL status
  const cibilFields = getDocFields(parsingView, 'cibil_report');
  const cibilHitFromReport = getDocField<string>(cibilFields, 'cibil_hit_status');
  const cibilScoreFromLos = (losData.data.applicants?.[0] as any)?.cibilScore as number | undefined;
  const cibilScoreFromReport = getDocField<number>(cibilFields, 'cibil_score');
  let cibilStatus = 'No-Hit';
  let cibilSource = 'LOS (default)';
  if (cibilHitFromReport) {
    cibilStatus = cibilHitFromReport.toLowerCase().includes('hit') && !cibilHitFromReport.toLowerCase().includes('no') ? 'Hit' : 'No-Hit';
    cibilSource = 'CIBIL Report PDF';
  } else if (cibilScoreFromLos != null && cibilScoreFromLos > 0) {
    cibilStatus = 'Hit';
    cibilSource = 'LOS (score > 0)';
  }
  const cibilScore = cibilScoreFromReport ?? cibilScoreFromLos ?? null;

  // Direct comparison: sanctioned vs maxLoanCapForCategory
  const withinCap = maxCap != null ? sanctioned <= maxCap : null;

  return (
    <table className="w-full text-caption">
      <thead>
        <tr className="text-text-tertiary border-b border-glass-hairline text-micro">
          <th className="text-left font-medium pb-1.5 w-[30%]">Parameter</th>
          <th className="text-left font-medium pb-1.5 w-[40%]">Value</th>
          <th className="text-left font-medium pb-1.5 w-[20%]">Source</th>
          <th className="text-right font-medium pb-1.5 w-[10%]">Verdict</th>
        </tr>
      </thead>
      <tbody>
        <tr className="border-b border-glass-hairline">
          <td className="py-1.5 font-medium text-text-secondary">Product</td>
          <td className="py-1.5">{losData.data.productCode ?? '—'}</td>
          <td className="py-1.5 text-text-tertiary">LOS</td>
          <td />
        </tr>
        <tr className="border-b border-glass-hairline">
          <td className="py-1.5 font-medium text-text-secondary">Company category</td>
          <td className="py-1.5 font-semibold">{category}</td>
          <td className="py-1.5 text-text-tertiary">LOS</td>
          <td />
        </tr>
        <tr className="border-b border-glass-hairline">
          <td className="py-1.5 font-medium text-text-secondary">CIBIL status</td>
          <td className="py-1.5">
            <span className={cibilStatus === 'Hit' ? 'text-emerald-700 font-semibold' : 'text-amber-700 font-semibold'}>
              {cibilStatus}
            </span>
            {cibilScore != null && <span className="text-text-tertiary ml-1">(Score: {cibilScore})</span>}
          </td>
          <td className="py-1.5 text-text-tertiary">{cibilSource}</td>
          <td />
        </tr>
        <tr className="border-b border-glass-hairline">
          <td className="py-1.5 font-medium text-text-secondary">Sanctioned amount</td>
          <td className="py-1.5 font-semibold text-label">{fmtInr(sanctioned)}</td>
          <td className="py-1.5 text-text-tertiary">LOS</td>
          <td />
        </tr>
        <tr className={"border-b border-glass-hairline " + (withinCap === false ? 'bg-red-50/40' : 'bg-emerald-50/30')}>
          <td className="py-2 font-medium text-text-secondary">
            Max loan cap (Cat {category})
          </td>
          <td className="py-2 font-semibold font-mono text-label">
            {fmtInr(maxCap)}
          </td>
          <td className="py-2 text-text-tertiary">
            Masters (company_categorization)
          </td>
          <td className="py-2 text-right">
            {withinCap === true
              ? <Badge tone="success">✓ Within cap</Badge>
              : withinCap === false
                ? <Badge tone="danger">✗ Exceeds</Badge>
                : <span className="text-text-tertiary">—</span>}
          </td>
        </tr>
      </tbody>
    </table>
  );
}


function PolicyLevelEvidence({ losData, parsingView, documents }: CamEvidenceDrawerProps) {
  const cam = getCamData(parsingView, documents);
  if (!losData) return <NoDataFallback what="LOS" />;

  const company = losData.data.companyCategorization;
  const sanctioned = losData.data.loanDetails?.sanctionedAmount ?? 0;

  const camCategory = cam.fields ? getCamField<string>(cam.fields, 'company_category') : null;
  const camCap = cam.fields ? getCamField<number>(cam.fields, 'company_cap_inr') : null;
  const camBreaches = cam.fields
    ? getCamField<string[]>(cam.fields, 'policy_breaches_noted') ?? []
    : [];

  // The rule uses Masters cap first, then falls back to LOS-declared cap.
  // Show both so the auditor understands which value the rule used.
  const losDeclaredCap = company?.maxLoanCapForCategory ?? null;

  return (
    <>
      <Section title="Company categorisation">
        <ComparisonTable rows={[
          {
            field: 'category',
            label: 'Category',
            losValue: company?.category ?? '—',
            camValue: camCategory ?? '—',
            match: company?.category !== undefined && camCategory !== null
              && normaliseText(company.category) === normaliseText(camCategory),
          },
          {
            field: 'cap',
            label: 'Max loan cap',
            losValue: fmtInr(losDeclaredCap),
            camValue: fmtInr(camCap),
            match: losDeclaredCap != null
              ? sanctioned <= losDeclaredCap
              : null,
          },
          {
            field: 'sanctioned',
            label: 'Sanctioned amount',
            losValue: fmtInr(sanctioned),
            camValue: cam.fields ? fmtInr(getCamField<number>(cam.fields, 'sanctioned_amount_inr')) : '—',
            match: losDeclaredCap != null
              ? sanctioned <= losDeclaredCap
              : null,
          },
        ]} />
      </Section>

      <Section title="Loan amount cap range (CIBIL Hit/No-Hit)">
        <LoanAmountCapCheck
          losData={losData}
          parsingView={parsingView}
        />
      </Section>

      <Section title="Cap check note">
        <div className="text-caption text-text-tertiary bg-slate-50/40 border border-glass-hairline rounded-md p-2.5">
          The audit rule checks the sanctioned amount against the cap from <span className="font-medium">Masters config</span> first
          (company_categorization table for Category {company?.category ?? '?'}). If no Masters entry exists, it falls back
          to the LOS-declared cap of {fmtInr(losDeclaredCap)}. If the verdict says a different cap value, it came from Masters.
        </div>
      </Section>

      {camBreaches.length > 0 && (
        <Section title="Policy breaches noted in CAM">
          <ul className="space-y-1 text-caption">
            {camBreaches.map((b, i) => (
              <li key={i} className="flex items-start gap-2">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-700 shrink-0 mt-0.5" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}
    </>
  );
}

// =========================================================================
// Point 8 — Deviation Matrix (bidirectional match + approver details)
// =========================================================================

function DeviationMatrixEvidence({ losData, parsingView, documents, loanFileId }: CamEvidenceDrawerProps) {
  const cam = getCamData(parsingView, documents);

  // Fetch all findings for this loan file (to get audit-found deviations)
  const findingsQuery = useQuery({
    queryKey: ['deviationFindings', loanFileId],
    queryFn: async () => {
      try {
        const { validationApi } = await import('@/api/validation-api');
        const res = await validationApi.listFindings({ loanFileId, pageSize: 100, page: 1 });
        return res.items ?? [];
      } catch { return []; }
    },
    enabled: !!loanFileId,
    staleTime: 30_000,
  });

  // Fetch deviation approvers from masters
  const approversQuery = useQuery({
    queryKey: ['deviationApprovers'],
    queryFn: async () => {
      try {
        const typeDetail = await mastersApi.getType('deviation_approvers');
        const results: Record<string, string[]> = {};
        for (const rec of typeDetail.records) {
          const detail = await mastersApi.getRecord(rec.id);
          const content = detail.content as { approverIds?: string[]; label?: string };
          results[rec.recordKey] = content.approverIds ?? [];
        }
        return results;
      } catch { return {} as Record<string, string[]>; }
    },
    staleTime: 60_000,
  });

  if (!cam.fields || !losData) return <NoDataFallback what={!cam.fields ? 'CAM' : 'LOS'} />;

  // ----- Source 1: Pre-existing deviations from LOS/CAM -----
  const losDevs = losData.data.deviations ?? [];


  type DeviationRow = {
    type: string;
    source: 'LOS/CAM' | 'Audit';
    level: string;
    approver: string;
    approverName: string;
    status: 'approved' | 'pending_approval' | 'rejected';
    mitigant: string;
    authorized: boolean | null;
    severity: string;
    description: string;
  };

  const rows: DeviationRow[] = [];
  const approverMap = approversQuery.data ?? {};

  // Add LOS deviations
  for (const d of losDevs) {
    const level = d.approvalLevel ?? '—';
    const approverId = d.approverId ?? '';
    const authorizedList = approverMap[level] ?? [];
    const isAuthorized = authorizedList.length > 0
      ? authorizedList.includes(approverId)
      : null; // null = no config for this level

    rows.push({
      type: d.deviationType,
      source: 'LOS/CAM',
      level,
      approver: approverId,
      approverName: d.approvedBy ?? '',
      status: (d.status as any) ?? 'approved',
      mitigant: d.mitigant ?? '',
      authorized: d.status === 'approved' ? isAuthorized : null,
      severity: 'high',
      description: d.mitigant ?? d.deviationType,
    });
  }

  // ----- Source 2: Audit-found deviations from mismatch findings -----
  // Filter audit-found mismatches (findings only exist for non-compliant verdicts).
  // Exclude the deviation matrix rule itself, and exclude findings with title
  // starting with "Deviation Approved:" (these are already shown in the
  // pre-existing section via the LOS deviations enumeration).
  const mismatchFindings = (findingsQuery.data ?? []).filter(
    (f: any) => {
      if (f.ruleId === 'cam.deviation_matrix') return false;
      const title = (f.title ?? '').toLowerCase();
      if (title.includes('deviation approved')) return false;
      if (f.status && f.status !== 'open') return false;
      // Show only deterministic violations (LLM-only disagreements not relevant here)
      return true;
    }
  );

  for (const f of mismatchFindings as any[]) {
    rows.push({
      type: f.ruleId?.replace('cam.', '') ?? 'unknown',
      source: 'Audit',
      level: '—',
      approver: '—',
      approverName: '—',
      status: 'pending_approval',
      mitigant: '',
      authorized: null,
      severity: f.severity ?? 'medium',
      description: f.title ?? f.description ?? '',
    });
  }

  if (rows.length === 0) {
    return (
      <Section title="Deviation Matrix">
        <div className="rounded-md border border-emerald-200 bg-emerald-50/40 p-3 text-caption text-emerald-800">
          No deviations on this loan file. LOS and CAM both record zero deviations,
          and the audit found no mismatches requiring deviation approval.
        </div>
      </Section>
    );
  }

  const preExisting = rows.filter((r) => r.source === 'LOS/CAM');
  const auditFound = rows.filter((r) => r.source === 'Audit');

  const statusBadge = (row: DeviationRow) => {
    if (row.status === 'approved') {
      if (row.authorized === true) return <Badge tone="success">Approved ✓</Badge>;
      if (row.authorized === false) return <Badge tone="danger">Unauthorized ✗</Badge>;
      return <Badge tone="success">Approved</Badge>;
    }
    if (row.status === 'pending_approval') return <Badge tone="warning">Pending</Badge>;
    return <Badge tone="danger">{row.status}</Badge>;
  };

  const formatType = (t: string) => t.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <>
      {preExisting.length > 0 && (
        <Section title={`Pre-existing Deviations — LOS/CAM (${preExisting.length})`}>
          <table className="w-full text-caption">
            <thead>
              <tr className="text-text-tertiary border-b border-glass-hairline text-micro">
                <th className="text-left font-medium pb-1.5">Deviation</th>
                <th className="text-left font-medium pb-1.5">Level</th>
                <th className="text-left font-medium pb-1.5">Approved By</th>
                <th className="text-left font-medium pb-1.5">Mitigant</th>
                <th className="text-right font-medium pb-1.5">Status</th>
              </tr>
            </thead>
            <tbody>
              {preExisting.map((row, i) => (
                <tr key={i} className="border-b border-glass-hairline last:border-0">
                  <td className="py-2 font-medium text-text-primary">{formatType(row.type)}</td>
                  <td className="py-2 text-text-secondary">
                    {row.level !== '—' ? (
                      <span className="inline-flex items-center gap-1">
                        <span className="bg-brand-blue/10 text-brand-blue text-micro font-semibold px-1.5 py-0.5 rounded">{row.level}</span>
                      </span>
                    ) : '—'}
                  </td>
                  <td className="py-2 text-text-secondary">
                    <div>{row.approverName || row.approver || '—'}</div>
                    {row.approver && row.approver !== '—' && row.approver !== row.approverName && (
                      <div className="text-micro text-text-tertiary">{row.approver}</div>
                    )}
                  </td>
                  <td className="py-2 text-text-secondary text-micro max-w-[200px] truncate">
                    {row.mitigant || <span className="text-red-500 italic">Not documented</span>}
                  </td>
                  <td className="py-2 text-right">{statusBadge(row)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {auditFound.length > 0 && (
        <Section title={`Audit-found Deviations (${auditFound.length})`}>
          <div className="text-micro text-text-tertiary mb-2">
            Mismatches detected by the audit that may require deviation approval.
          </div>
          <table className="w-full text-caption">
            <thead>
              <tr className="text-text-tertiary border-b border-glass-hairline text-micro">
                <th className="text-left font-medium pb-1.5">Finding</th>
                <th className="text-left font-medium pb-1.5">Severity</th>
                <th className="text-left font-medium pb-1.5">Description</th>
                <th className="text-right font-medium pb-1.5">Status</th>
              </tr>
            </thead>
            <tbody>
              {auditFound.map((row, i) => (
                <tr key={i} className="border-b border-glass-hairline last:border-0">
                  <td className="py-2 font-medium text-text-primary">{formatType(row.type)}</td>
                  <td className="py-2">
                    <Badge tone={row.severity === 'critical' || row.severity === 'high' ? 'danger' : 'warning'}>
                      {row.severity}
                    </Badge>
                  </td>
                  <td className="py-2 text-text-secondary text-micro max-w-[250px] truncate">{row.description}</td>
                  <td className="py-2 text-right">{statusBadge(row)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {preExisting.length === 0 && auditFound.length === 0 && (
        <Section title="Deviation Matrix">
          <div className="rounded-md border border-emerald-200 bg-emerald-50/40 p-3 text-caption text-emerald-800">
            No deviations found.
          </div>
        </Section>
      )}
    </>
  );
}


function SanctionConditionsEvidence({ losData, parsingView, documents, finding }: CamEvidenceDrawerProps) {
  const cam = getCamData(parsingView, documents);
  if (!cam.fields || !losData) return <NoDataFallback what={!cam.fields ? 'CAM' : 'LOS'} />;

  const losConditions: string[] = losData.data.sanctionConditions ?? [];
  const camConditions: string[] = getCamField<string[]>(cam.fields, 'sanction_conditions') ?? [];

  // Extract match results from the finding evidence (computed by the rule)
  const extra = (finding?.evidence as any)?.extra ?? {};
  const camMatches: Array<{
    camIndex: number; camText: string;
    bestLosIndex: number; bestLosText: string;
    similarity: number; matched: boolean;
  }> = extra.camMatches ?? [];
  const unmatchedLos: Array<{ losIndex: number; losText: string }> = extra.unmatchedLos ?? [];

  // If no match data from rule (e.g. compliant finding has no evidence),
  // compute basic word-overlap client-side for display
  const hasMatchData = camMatches.length > 0 || (camConditions.length === 0 && losConditions.length === 0);

  return (
    <>
      {/* Per-condition match table */}
      {hasMatchData && camMatches.length > 0 && (
        <Section title="Condition-by-condition comparison">
          <div className="space-y-2">
            {camMatches.map((m, i) => (
              <div
                key={i}
                className={`rounded-md border p-2.5 text-caption ${
                  m.matched
                    ? 'border-emerald-200 bg-emerald-50/40'
                    : 'border-red-200 bg-red-50/40'
                }`}
              >
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <span className="text-micro font-semibold text-text-tertiary">
                    CAM condition {m.camIndex + 1}
                  </span>
                  <span className={`text-micro font-semibold ${m.matched ? 'text-emerald-700' : 'text-red-700'}`}>
                    {m.matched ? `✓ Matched (${m.similarity}%)` : `✗ No match (${m.similarity}%)`}
                  </span>
                </div>
                <div className="text-text-primary leading-snug">{m.camText}</div>
                {m.matched && m.bestLosText && (
                  <div className="mt-1.5 pt-1.5 border-t border-glass-hairline">
                    <span className="text-micro text-text-tertiary">
                      Best LOS match (#{m.bestLosIndex + 1}):
                    </span>
                    <div className="text-text-secondary mt-0.5">{m.bestLosText}</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Unmatched LOS conditions */}
      {unmatchedLos.length > 0 && (
        <Section title={`Unmatched LOS conditions (${unmatchedLos.length})`}>
          <div className="space-y-1.5">
            {unmatchedLos.map((u) => (
              <div key={u.losIndex} className="rounded-md border border-amber-200 bg-amber-50/40 p-2.5 text-caption">
                <span className="text-micro font-semibold text-amber-700">
                  LOS #{u.losIndex + 1} — no CAM counterpart
                </span>
                <div className="text-text-primary mt-0.5 leading-snug">{u.losText}</div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Full lists (always visible) */}
      <Section title={`All LOS conditions (${losConditions.length})`}>
        {losConditions.length === 0 ? (
          <EmptyHint>No conditions recorded in LOS.</EmptyHint>
        ) : (
          <ol className="space-y-1 list-decimal list-inside text-caption text-text-primary">
            {losConditions.map((c, i) => <li key={i} className="leading-snug">{c}</li>)}
          </ol>
        )}
      </Section>
      <Section title={`All CAM conditions (${camConditions.length})`}>
        {camConditions.length === 0 ? (
          <EmptyHint>No conditions recorded in CAM.</EmptyHint>
        ) : (
          <ol className="space-y-1 list-decimal list-inside text-caption text-text-primary">
            {camConditions.map((c, i) => <li key={i} className="leading-snug">{c}</li>)}
          </ol>
        )}
      </Section>
    </>
  );
}

// =========================================================================
// Point 10 — Legal Detail
// =========================================================================

function LegalDetailEvidence({ parsingView, documents, losData }: CamEvidenceDrawerProps) {
  const cam = getCamData(parsingView, documents);
  if (!cam.fields) return <NoDataFallback what="CAM" />;

  const isSecured = getCamField<boolean>(cam.fields, 'is_secured');
  if (isSecured === false) {
    return (
      <Section title="Not applicable">
        <EmptyHint>
          This is an <strong>unsecured loan</strong> (Personal Loan / Business Loan) — Legal Detail check does not apply.
          This check runs only for <strong>secured products</strong>: Home Loan, Machine Loan, Disha HL, Micro LAP — where collateral property is offered and a legal title investigation is required.
        </EmptyHint>
      </Section>
    );
  }

  const legalStatus = getCamField<string>(cam.fields, 'legal_check_status');
  const propName = getCamField<string>(cam.fields, 'collateral_property_name');
  const propAddress = getCamField<string>(cam.fields, 'collateral_property_address');
  const ownerName = getCamField<string>(cam.fields, 'collateral_owner_name');

  const losLegal = losData?.data?.legalDetail ?? null;
  const losLegalStatus = losLegal?.legalCheckStatus ?? null;
  const losPropName = losLegal?.propertyName ?? null;
  const losPropAddress = losLegal?.propertyAddress ?? null;
  const losOwnerName = losLegal?.ownerName ?? null;

  // Extract Legal Report PDF fields (3rd source)
  const lrFields = getDocFields(parsingView, 'legal_technical_report');
  const lrStatus = getDocField<string>(lrFields, 'legal_check_status');
  const lrPropName = getDocField<string>(lrFields, 'property_name');
  const lrOwnerName = getDocField<string>(lrFields, 'owner_name');
  const lrPropAddress = getDocField<string>(lrFields, 'property_address');
  const legalReportDoc = documents.find((d) => d.documentType === 'legal_technical_report');
  const hasLegalReport = lrFields !== null;

  const normalise = (s: string | null | undefined) => (s ?? '').trim().toLowerCase();

  // Fuzzy match helper — handles abbreviations, spaces, punctuation
  const fuzzyMatch = (a: string, b: string): boolean => {
    if (a === b) return true;
    // Deep normalise: expand abbreviations, strip all non-alphanumeric
    const deep = (s: string) => {
      let t = s.toLowerCase();
      t = t.replace(/\bsec\b/g, 'sector').replace(/\bno\b/g, 'number');
      t = t.replace(/\bfl\b/g, 'flat').replace(/\brd\b/g, 'road');
      t = t.replace(/\bmrs?\b/g, '').replace(/\bsmt\b/g, '');
      return t.replace(/[^a-z0-9]/g, '');
    };
    const da = deep(a), db = deep(b);
    if (da === db) return true;
    if (da.includes(db) || db.includes(da)) return true;
    // Bigram Dice coefficient
    const bigrams = (s: string) => {
      const set = new Set<string>();
      for (let i = 0; i < s.length - 1; i++) set.add(s.substring(i, i + 2));
      return set;
    };
    const bg1 = bigrams(da), bg2 = bigrams(db);
    if (bg1.size === 0 || bg2.size === 0) return da === db;
    let inter = 0;
    for (const b of bg1) { if (bg2.has(b)) inter++; }
    return (2 * inter) / (bg1.size + bg2.size) >= 0.80;
  };

  // 3-way match: all non-null sources must agree (fuzzy)
  const match3 = (...vals: (string | null | undefined)[]) => {
    const defined = vals.filter((v) => v != null) as string[];
    if (defined.length < 2) return null;
    // All pairs must fuzzy-match
    for (let i = 1; i < defined.length; i++) {
      if (!fuzzyMatch(normalise(defined[0]), normalise(defined[i]))) return false;
    }
    return true;
  };

  const statusAllMatch = match3(losLegalStatus, legalStatus, lrStatus);
  const propNameAllMatch = match3(losPropName, propName, lrPropName);
  const addressAllMatch = match3(losPropAddress, propAddress, lrPropAddress);
  const ownerAllMatch = match3(losOwnerName, ownerName, lrOwnerName);

  return (
    <>
      <Section title="Legal status — 3-way comparison">
        <ThreeWayTable
          hasReport={hasLegalReport}
          reportLabel="Legal Report"
          rows={[
            {
              label: 'Legal check status',
              losValue: losLegalStatus ?? '—',
              camValue: legalStatus ?? '—',
              reportValue: lrStatus ?? '—',
              match: statusAllMatch ?? (normalise(legalStatus) === 'positive'),
            },
          ]}
        />
      </Section>

      <Section title="Property details — 3-way comparison">
        <ThreeWayTable
          hasReport={hasLegalReport}
          reportLabel="Legal Report"
          rows={[
            {
              label: 'Property name',
              losValue: losPropName ?? '—',
              camValue: propName ?? '—',
              reportValue: lrPropName ?? '—',
              match: propNameAllMatch,
            },
            {
              label: 'Property address',
              losValue: losPropAddress ?? '—',
              camValue: propAddress ?? '—',
              reportValue: lrPropAddress ?? '—',
              match: addressAllMatch,
            },
            {
              label: 'Owner name',
              losValue: losOwnerName ?? '—',
              camValue: ownerName ?? '—',
              reportValue: lrOwnerName ?? '—',
              match: ownerAllMatch,
            },
          ]}
        />
      </Section>

      {!losLegal && (
        <Section title="LOS legal data missing">
          <EmptyHint>
            The LOS JSON does not include a <code className="font-mono text-micro bg-slate-100 px-1 rounded">legalDetail</code> section.
            Add it to the LOS JSON to enable LOS vs CAM vs Legal Report comparison.
          </EmptyHint>
        </Section>
      )}

      {legalReportDoc ? (
        <Section title="Legal Report on file">
          <div className="text-caption text-text-secondary">
            ✓ Legal Report uploaded: <span className="font-medium">{legalReportDoc.originalFileName}</span>
            {hasLegalReport
              ? ' — extracted fields shown above in the 3-way comparison.'
              : ' — parsing in progress or extraction pending.'}
          </div>
        </Section>
      ) : (
        <Section title="Legal Report not uploaded">
          <EmptyHint>
            Without the Legal Report PDF, verification is limited to LOS vs CAM.
            Upload the Legal Report to enable full three-way verification.
          </EmptyHint>
        </Section>
      )}
    </>
  );
}

// =========================================================================
// Point 11 — Valuation Report
// =========================================================================

function ValuationReportEvidence({ losData, parsingView, documents }: CamEvidenceDrawerProps) {
  const cam = getCamData(parsingView, documents);
  if (!cam.fields) return <NoDataFallback what="CAM" />;

  const isSecured = getCamField<boolean>(cam.fields, 'is_secured');
  if (isSecured === false) {
    return (
      <Section title="Not applicable">
        <EmptyHint>
          This is an <strong>unsecured loan</strong> (Personal Loan / Business Loan) — Valuation Report check does not apply.
          This check runs only for <strong>secured products</strong>: Home Loan, Machine Loan, Disha HL, Micro LAP — where collateral property requires independent valuation.
        </EmptyHint>
      </Section>
    );
  }

  // CAM values
  const camValAmount = getCamField<number>(cam.fields, 'valuation_amount_inr');
  const camValDate = getCamField<string>(cam.fields, 'valuation_date');
  const camValuerName = getCamField<string>(cam.fields, 'valuer_name');
  const camPropName = getCamField<string>(cam.fields, 'collateral_property_name');
  const camPropAddress = getCamField<string>(cam.fields, 'collateral_property_address');

  // LOS values
  const losVal = losData?.data?.valuationDetail ?? null;
  const losValAmount = losVal?.valuationAmountInr ?? null;
  const losValDate = losVal?.valuationDate ?? null;
  const losValuerName = losVal?.valuerName ?? null;
  const losPropName = losVal?.propertyName ?? null;
  const losPropAddress = losVal?.propertyAddress ?? null;

  // Extract Valuation Report PDF fields (3rd source)
  const vrFields = getDocFields(parsingView, 'valuation');
  const vrAmount = getDocField<number>(vrFields, 'valuation_amount_inr');
  const vrPropName = getDocField<string>(vrFields, 'property_name');
  const vrValDate = getDocField<string>(vrFields, 'valuation_date');
  const vrValuerName = getDocField<string>(vrFields, 'valuer_name');
  const vrPropAddress = getDocField<string>(vrFields, 'property_address');
  const valReportDoc = documents.find((d) => d.documentType === 'valuation');
  const hasValReport = vrFields !== null;

  const normalise = (s: string | null | undefined) => (s ?? '').trim().toLowerCase();

  const fuzzyMatchVal = (a: string, b: string): boolean => {
    if (a === b) return true;
    const deep = (s: string) => {
      let t = s.toLowerCase();
      t = t.replace(/\bsec\b/g, 'sector').replace(/\bno\b/g, 'number');
      return t.replace(/[^a-z0-9]/g, '');
    };
    const da = deep(a), db = deep(b);
    if (da === db || da.includes(db) || db.includes(da)) return true;
    const bigrams = (s: string) => { const set = new Set<string>(); for (let i = 0; i < s.length - 1; i++) set.add(s.substring(i, i + 2)); return set; };
    const bg1 = bigrams(da), bg2 = bigrams(db);
    if (bg1.size === 0 || bg2.size === 0) return da === db;
    let inter = 0; for (const b of bg1) { if (bg2.has(b)) inter++; }
    return (2 * inter) / (bg1.size + bg2.size) >= 0.80;
  };

  const match3str = (...vals: (string | null | undefined)[]) => {
    const defined = vals.filter((v) => v != null) as string[];
    if (defined.length < 2) return null;
    for (let i = 1; i < defined.length; i++) {
      if (!fuzzyMatchVal(normalise(defined[0]), normalise(defined[i]))) return false;
    }
    return true;
  };

  const match3num = (...vals: (number | null | undefined)[]) => {
    const defined = vals.filter((v) => v != null) as number[];
    if (defined.length < 2) return null;
    return defined.every((v) => v === defined[0]);
  };

  // 90-day validity
  const disbDate = losData?.data?.dates?.disbursementDate ?? null;
  const effectiveValDate = camValDate ?? losValDate;
  let valuationAgeDays: number | null = null;
  if (effectiveValDate && disbDate) {
    const a = Date.parse(effectiveValDate);
    const b = Date.parse(disbDate);
    if (Number.isFinite(a) && Number.isFinite(b)) {
      valuationAgeDays = Math.floor((b - a) / (1000 * 60 * 60 * 24));
    }
  }

  return (
    <>
      <Section title="Valuation details — 3-way comparison">
        <ThreeWayTable
          hasReport={hasValReport}
          reportLabel="Valuation Report"
          rows={[
            {
              label: 'Property name',
              losValue: losPropName ?? '—',
              camValue: camPropName ?? '—',
              reportValue: vrPropName ?? '—',
              match: match3str(losPropName, camPropName, vrPropName),
            },
            {
              label: 'Property address',
              losValue: losPropAddress ?? '—',
              camValue: camPropAddress ?? '—',
              reportValue: vrPropAddress ?? '—',
              match: match3str(losPropAddress, camPropAddress),
            },
            {
              label: 'Valuation amount',
              losValue: fmtInr(losValAmount),
              camValue: fmtInr(camValAmount),
              reportValue: fmtInr(vrAmount),
              match: match3num(losValAmount, camValAmount, vrAmount),
            },
            {
              label: 'Valuation date',
              losValue: fmtDate(losValDate),
              camValue: fmtDate(camValDate),
              reportValue: fmtDate(vrValDate),
              match: match3str(losValDate, camValDate, vrValDate),
            },
            {
              label: 'Valuer name',
              losValue: losValuerName ?? '—',
              camValue: camValuerName ?? '—',
              reportValue: vrValuerName ?? '—',
              match: match3str(losValuerName, camValuerName, vrValuerName),
            },
          ]}
        />
      </Section>

      <Section title="90-day validity check">
        <ThreeWayTable
          hasReport={false}
          reportLabel=""
          rows={[
            {
              label: 'Disbursement date',
              losValue: fmtDate(disbDate),
              camValue: '—',
              reportValue: '—',
              match: null,
            },
            {
              label: 'Valuation date (used)',
              losValue: fmtDate(losValDate),
              camValue: fmtDate(camValDate),
              reportValue: fmtDate(vrValDate),
              match: null,
            },
            {
              label: 'Valuation age at disbursement',
              losValue: valuationAgeDays !== null ? `${valuationAgeDays} days` : '—',
              camValue: 'max 90 days',
              reportValue: '—',
              match: valuationAgeDays !== null ? valuationAgeDays <= 90 : null,
            },
          ]}
        />
      </Section>

      {!losVal && (
        <Section title="LOS valuation data missing">
          <EmptyHint>
            The LOS JSON does not include a <code className="font-mono text-micro bg-slate-100 px-1 rounded">valuationDetail</code> section.
            Add it to enable LOS vs CAM vs Valuation Report comparison.
          </EmptyHint>
        </Section>
      )}

      {valReportDoc ? (
        <Section title="Valuation Report on file">
          <div className="text-caption text-text-secondary">
            ✓ Valuation Report uploaded: <span className="font-medium">{valReportDoc.originalFileName}</span>
            {hasValReport
              ? ' — extracted fields shown above in the 3-way comparison.'
              : ' — parsing in progress or extraction pending.'}
          </div>
        </Section>
      ) : (
        <Section title="Valuation Report not uploaded">
          <EmptyHint>
            Without the Valuation Report PDF, verification is limited to LOS vs CAM.
            Upload the Valuation Report for full three-way verification.
          </EmptyHint>
        </Section>
      )}
    </>
  );
}

// =========================================================================
// Point 12 — Personal Discussion
// =========================================================================

function PersonalDiscussionEvidence({ parsingView, documents }: CamEvidenceDrawerProps) {
  const cam = getCamData(parsingView, documents);
  if (!cam.fields) return <NoDataFallback what="CAM" />;

  const conducted = getCamField<boolean>(cam.fields, 'pd_conducted');
  const date = getCamField<string>(cam.fields, 'pd_date');
  const conductedBy = getCamField<string>(cam.fields, 'pd_conducted_by');
  const empConfirmed = getCamField<boolean>(cam.fields, 'pd_employment_confirmed');
  const intentConfirmed = getCamField<boolean>(cam.fields, 'pd_intent_confirmed');
  const familyNoted = getCamField<boolean>(cam.fields, 'pd_family_context_noted');
  const notes = getCamField<string>(cam.fields, 'pd_notes');

  return (
    <>
      <Section title="PD metadata">
        <table className="w-full text-caption">
          <tbody>
            <tr className="border-b border-glass-hairline">
              <td className="py-1.5 font-medium w-1/3">PD conducted</td>
              <td className="py-1.5">{fmtBool(conducted)}</td>
            </tr>
            <tr className="border-b border-glass-hairline">
              <td className="py-1.5 font-medium">PD date</td>
              <td className="py-1.5">{fmtDate(date)}</td>
            </tr>
            <tr>
              <td className="py-1.5 font-medium">Conducted by</td>
              <td className="py-1.5">{conductedBy ?? <span className="text-text-tertiary">—</span>}</td>
            </tr>
          </tbody>
        </table>
      </Section>

      <Section title="Substance flags">
        <ul className="space-y-1 text-caption">
          <li className="flex items-center gap-2">
            {empConfirmed ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700" />
              : <XCircle className="w-3.5 h-3.5 text-red-700" />}
            Employment confirmed
          </li>
          <li className="flex items-center gap-2">
            {intentConfirmed ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700" />
              : <XCircle className="w-3.5 h-3.5 text-red-700" />}
            Loan intent confirmed
          </li>
          <li className="flex items-center gap-2">
            {familyNoted ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700" />
              : <XCircle className="w-3.5 h-3.5 text-red-700" />}
            Family context noted
          </li>
        </ul>
      </Section>

      <Section title="PD notes">
        {notes ? (
          <div className="rounded-md border border-glass-hairline bg-slate-50/50 p-3 text-caption text-text-primary whitespace-pre-wrap">
            {notes.length > 400 ? notes.slice(0, 400) + '…' : notes}
          </div>
        ) : (
          <EmptyHint>No PD notes captured in CAM.</EmptyHint>
        )}
      </Section>
    </>
  );
}

// =========================================================================
// ThreeWayTable — 3-column comparison table (LOS vs CAM vs Report PDF)
// Used by Point 10 (Legal) and Point 11 (Valuation) evidence renderers.
// When hasReport is false, the Report column is hidden gracefully.
// =========================================================================

function ThreeWayTable({
  rows,
  hasReport,
  reportLabel,
}: {
  rows: Array<{
    label: string;
    losValue: ReactNode;
    camValue: ReactNode;
    reportValue: ReactNode;
    match: boolean | null;
  }>;
  hasReport: boolean;
  reportLabel: string;
}) {
  if (rows.length === 0) return <EmptyHint>No comparable fields.</EmptyHint>;
  return (
    <div className="overflow-x-auto">
    <table className="w-full text-caption">
      <thead>
        <tr className="text-text-tertiary border-b border-glass-hairline text-micro">
          <th className="text-left font-medium pb-1.5">Field</th>
          <th className="text-left font-medium pb-1.5">LOS</th>
          <th className="text-left font-medium pb-1.5">CAM</th>
          {hasReport && (
            <th className="text-left font-medium pb-1.5">
              <span className="inline-flex items-center gap-1">
                {reportLabel}
                <span className="text-micro font-normal text-brand-blue bg-brand-blue/[0.06] px-1 rounded">PDF</span>
              </span>
            </th>
          )}
          <th className="text-right font-medium pb-1.5">Verdict</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-b border-glass-hairline last:border-0">
            <td className="py-1.5 align-top font-medium text-text-primary">{r.label}</td>
            <td className="py-1.5 align-top font-mono text-micro text-text-primary">
              {r.losValue ?? <span className="text-text-tertiary">—</span>}
            </td>
            <td className="py-1.5 align-top font-mono text-micro text-text-primary">
              {r.camValue ?? <span className="text-text-tertiary">—</span>}
            </td>
            {hasReport && (
              <td className="py-1.5 align-top font-mono text-micro text-text-primary">
                {r.reportValue ?? <span className="text-text-tertiary">—</span>}
              </td>
            )}
            <td className="py-1.5 align-top text-right">
              {r.match === true ? (
                <Badge tone="success">✓</Badge>
              ) : r.match === false ? (
                <Badge tone="danger">✗</Badge>
              ) : (
                <span className="text-text-tertiary">—</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    </div>
  );
}











